import React, { useEffect, useRef, useState } from 'react';
import { bookingMessagesAction } from '../lib/callables.js';
import { AUTH_MODE } from '../auth/firebase.js';

function MessageText({ text }) {
    return text.split(/(https?:\/\/[^\s<>]+)/g).map((part, i) => /^https?:\/\//.test(part)
        ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline text-book-accent break-all">{part}</a> : part);
}

export default function BookingMessages({ request }) {
    const [open, setOpen] = useState(false), [data, setData] = useState({ unread: 0 });
    const [text, setText] = useState(''), [error, setError] = useState(''), [busy, setBusy] = useState(false);
    const generation = useRef(0);
    const available = ['confirmed', 'completed'].includes(request.status) && !request.archived;
    const load = async (expanded, version) => {
        try {
            const next = await bookingMessagesAction({ requestId: request.id, action: expanded ? 'list' : 'summary' });
            if (generation.current !== version) return;
            setData(next); setError('');
            if (expanded && next.unread) {
                await bookingMessagesAction({ requestId: request.id, action: 'read', through: next.through });
                if (generation.current === version) setData(d => ({ ...d, unread: 0 }));
            }
        } catch (e) { if (generation.current === version) setError(e.message || 'Could not load messages.'); }
    };
    useEffect(() => {
        const version = ++generation.current;
        if (!available || AUTH_MODE === 'mock') return;
        load(open, version);
        const timer = setInterval(() => { if (!document.hidden) load(open, version); }, 60000);
        return () => { clearInterval(timer); generation.current++; };
    }, [request.id, request.status, available, open]);
    if (!available) return null;
    const send = async e => {
        e.preventDefault(); setBusy(true); setError('');
        try {
            await bookingMessagesAction({ requestId: request.id, action: 'send', text });
            setText(''); await load(true, generation.current);
        } catch (e) { setError(e.message || 'Could not send. Your draft is kept; please retry.'); }
        finally { setBusy(false); }
    };
    return <section className="mt-3 border-t border-book-line pt-3 text-book-text selectable">
        <button type="button" aria-expanded={open} className="text-sm text-book-accent" onClick={() => setOpen(v => !v)}>
            {open ? '▾ Hide booking messages' : '▸ Booking messages'}{data.unread > 0 && <span className="ml-2 rounded-full bg-book-accent text-white px-2" aria-label={`${data.unread} unread messages`}>{data.unread}</span>}
        </button>
        {error && <p role="alert" className="text-sm mt-2">{error} <button className="underline" onClick={() => load(open, generation.current)}>Retry</button></p>}
        {open && <div className="mt-3 space-y-3">
            <p className="text-xs text-book-muted">Messages with Teaching Services · latest 50 messages · checks every minute.</p>
            <div className="max-h-72 overflow-y-auto space-y-2" aria-live="polite">
                {!data.messages ? <p>Loading…</p> : data.messages.length === 0 ? <p className="text-sm text-book-muted">No messages yet. Share a question or your Zoom meeting details here.</p> : data.messages.map(m => <article key={m.id} className="rounded-xl border border-book-line p-3 text-sm">
                    <p className="text-xs text-book-muted mb-1">{m.name} · {new Date(m.at).toLocaleString()}</p>
                    <p className="whitespace-pre-wrap break-words"><MessageText text={m.text} /></p>
                </article>)}
            </div>
            {data.canSend && <form onSubmit={send} className="space-y-2">
                <textarea aria-label="Booking message" value={text} maxLength={2000} rows={3} onChange={e => setText(e.target.value)} className="w-full min-w-0 rounded-xl border border-book-line bg-book-bg p-3 text-sm" />
                <button disabled={busy || !text.trim()} className="px-4 py-2 rounded-full border border-book-line text-book-accent disabled:opacity-50">{busy ? 'Sending…' : 'Send message'}</button>
            </form>}
        </div>}
    </section>;
}
